/**
 * What the site says about one headword, read over HTTP.
 *
 * Every 词典 entry publishes `/cidian/<word>/canonical.json` beside its page,
 * about 420 bytes of word, reading, meanings and band. `src/user/` reads the
 * same object out of the bucket when a word is saved. The extension's panel
 * and the segmenter's word list both read it over the wire instead, where a
 * 404 is the site's answer to "no entry for that".
 *
 * Read defensively because it arrives over the network from a cache that may
 * be a year old. Every field is checked and a malformed object reads as no
 * entry, which is the same answer a 404 gives and needs no second path.
 *
 * **The meanings are the page's wording where the object carries it.**
 * `meaningsShown` is published beside `meanings` for an entry whose glosses
 * cite another word, and says `欧罗巴洲 ōuluóbāzhōu` where CC-CEDICT says
 * `歐羅巴洲|欧罗巴洲[Ou1 luo2 ba1 Zhou1]`. `cidian-canonical-object.ts` has
 * why it is sparse. An object from before it was published, or one citing
 * nothing, has only `meanings`, and those are already what the page says. A
 * `meaningsShown` that is no list is read as absent, so a malformed one leaves
 * the glosses in `meanings` standing.
 */

/** One 词典 entry, as much of it as a panel or a word list shows. */
export interface CanonicalEntry {
  /** The headword, in the script the entry is filed under. */
  readonly word: string;

  /** Its reading, written with tone marks. */
  readonly pinyin: string;

  /** The glosses as the 词典 page words them, in the order it lists them. */
  readonly meanings: readonly string[];

  /** The HSK band the entry carries, where it is graded. */
  readonly band: string | undefined;
}

/** Where the entry for a headword is published. */
export function canonicalAddress(word: string): string {
  return `/cidian/${encodeURIComponent(word)}/canonical.json`;
}

/**
 * The entry an object describes, or nothing where it describes none.
 *
 * `hsk` is an empty string on an ungraded entry rather than absent, which is
 * how the published object spells it, so an empty band reads as no band.
 */
export function canonicalEntry(body: unknown): CanonicalEntry | undefined {
  if (typeof body !== "object" || body === null) {
    return undefined;
  }

  const fields = body as Record<string, unknown>;
  const word = fields["word"];
  const pinyin = fields["pinyin"];
  const band = fields["hsk"];
  const shown = fields["meaningsShown"];

  if (typeof word !== "string" || word === "" || typeof pinyin !== "string") {
    return undefined;
  }

  return {
    word,
    pinyin,
    meanings: meaningsOf(Array.isArray(shown) ? shown : fields["meanings"]),
    band: typeof band === "string" && band !== "" ? band : undefined,
  };
}

/** The glosses in an entry, dropping anything that is not one. */
function meaningsOf(meanings: unknown): readonly string[] {
  if (!Array.isArray(meanings)) {
    return [];
  }

  return meanings.filter(
    (meaning): meaning is string => typeof meaning === "string",
  );
}
